import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { 
  Calculator,
  Footprints,
  Scale,
  Activity,
  Flame,
  Heart,
  Target,
  Ruler,
  Info,
  ChevronLeft
} from 'lucide-react';

const CalculatorsPage = ({ onBack }) => {
  // Schema.org JSON-LD structured data for SEO
  useEffect(() => {
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.nutristeps.me/calculators",
          "url": "https://www.nutristeps.me/calculators",
          "name": "Free Fitness & Health Calculators - BMR, Body Fat, TDEE, Calories",
          "description": "Free online fitness calculators: BMR calculator, body fat percentage calculator, TDEE calculator, lean body mass calculator, ideal weight calculator, walking calories calculator. No signup required.",
          "isPartOf": {
            "@type": "WebSite",
            "@id": "https://www.nutristeps.me/#website",
            "name": "NutriSteps",
            "url": "https://www.nutristeps.me"
          },
          "about": {
            "@type": "Thing",
            "name": "Fitness Calculators"
          },
          "mainEntity": {
            "@id": "https://www.nutristeps.me/calculators#calculators"
          }
        },
        {
          "@type": "ItemList",
          "@id": "https://www.nutristeps.me/calculators#calculators",
          "name": "Fitness Calculators",
          "description": "Collection of free online fitness and health calculators",
          "numberOfItems": 6,
          "itemListElement": [
            {
              "@type": "SoftwareApplication",
              "position": 1,
              "name": "BMR Calculator",
              "alternateName": "Basal Metabolic Rate Calculator",
              "description": "Calculate your Basal Metabolic Rate (BMR) - how many calories your body burns at rest. Uses the accurate Mifflin-St Jeor equation.",
              "applicationCategory": "HealthApplication",
              "operatingSystem": "Web Browser",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              },
              "featureList": ["BMR calculation", "TDEE estimation", "Activity level multipliers", "Mifflin-St Jeor formula"]
            },
            {
              "@type": "SoftwareApplication",
              "position": 2,
              "name": "Body Fat Percentage Calculator",
              "alternateName": "Navy Body Fat Calculator",
              "description": "Estimate your body fat percentage using the US Navy method. More accurate than BMI for assessing body composition.",
              "applicationCategory": "HealthApplication",
              "operatingSystem": "Web Browser",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              },
              "featureList": ["Body fat percentage", "Fat mass calculation", "Lean mass calculation", "US Navy method"]
            },
            {
              "@type": "SoftwareApplication",
              "position": 3,
              "name": "Lean Body Mass Calculator",
              "alternateName": "LBM Calculator",
              "description": "Calculate your lean body mass (LBM) - total weight minus fat. Essential for tracking muscle gain and body recomposition.",
              "applicationCategory": "HealthApplication",
              "operatingSystem": "Web Browser",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              },
              "featureList": ["Lean body mass", "Fat mass", "Lean percentage", "Muscle tracking"]
            },
            {
              "@type": "SoftwareApplication",
              "position": 4,
              "name": "Daily Calorie Calculator",
              "alternateName": "TDEE Calculator",
              "description": "Find how many calories you need daily based on activity level. Shows maintenance, weight loss, and weight gain calories.",
              "applicationCategory": "HealthApplication",
              "operatingSystem": "Web Browser",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              },
              "featureList": ["TDEE calculation", "Calorie deficit", "Calorie surplus", "Activity level adjustment"]
            },
            {
              "@type": "SoftwareApplication",
              "position": 5,
              "name": "Ideal Body Weight Calculator",
              "alternateName": "Healthy Weight Calculator",
              "description": "Calculate your ideal weight for your height using the Devine formula. Includes healthy BMI weight range.",
              "applicationCategory": "HealthApplication",
              "operatingSystem": "Web Browser",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              },
              "featureList": ["Ideal weight", "Devine formula", "BMI healthy range", "Height-based calculation"]
            },
            {
              "@type": "SoftwareApplication",
              "position": 6,
              "name": "Walking Calories Calculator",
              "alternateName": "Calories Burned Walking Calculator",
              "description": "Calculate how many calories walking burns based on distance and your weight. Includes time estimates at different speeds.",
              "applicationCategory": "HealthApplication",
              "operatingSystem": "Web Browser",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              },
              "featureList": ["Calories burned", "Distance-based", "Walking speed estimates", "Weight-adjusted"]
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.nutristeps.me/calculators#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How accurate is the BMR calculator?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Our BMR calculator uses the Mifflin-St Jeor equation, considered the most accurate formula for estimating basal metabolic rate. It's typically within 10% of actual BMR for most people."
              }
            },
            {
              "@type": "Question",
              "name": "What's the difference between BMR and TDEE?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "BMR (Basal Metabolic Rate) is calories burned at complete rest. TDEE (Total Daily Energy Expenditure) includes your activity level - it's your actual daily calorie burn and what you should base your diet on."
              }
            },
            {
              "@type": "Question",
              "name": "How many calories should I eat to lose weight?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "For safe weight loss, eat 500 calories below your TDEE. This creates a calorie deficit of 3,500 calories per week, resulting in approximately 0.5kg (1 lb) of fat loss weekly."
              }
            },
            {
              "@type": "Question",
              "name": "Is body fat percentage better than BMI?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, body fat percentage is more accurate than BMI for assessing health. BMI doesn't distinguish between muscle and fat, so muscular people may have 'overweight' BMI despite being healthy."
              }
            }
          ]
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://www.nutristeps.me"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Calculators",
              "item": "https://www.nutristeps.me/calculators"
            }
          ]
        }
      ]
    };

    // Create or update the schema script
    let schemaScript = document.getElementById('calculators-schema');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.id = 'calculators-schema';
      schemaScript.type = 'application/ld+json';
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify(schemaData);

    // Update page title and meta description
    const originalTitle = document.title;
    const originalDescription = document.querySelector('meta[name="description"]')?.content;
    
    document.title = 'Free Fitness Calculators - BMR, Body Fat %, TDEE, Calories | NutriSteps';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.content = 'Free online fitness calculators: BMR calculator, body fat percentage calculator, TDEE calculator, lean body mass, ideal weight, walking calories burned. No signup required. Accurate formulas.';
    }

    // Cleanup on unmount
    return () => {
      const script = document.getElementById('calculators-schema');
      if (script) {
        script.remove();
      }
      document.title = originalTitle;
      if (metaDescription && originalDescription) {
        metaDescription.content = originalDescription;
      }
    };
  }, []);

  // BMR Calculator State
  const [bmrWeight, setBmrWeight] = useState('');
  const [bmrHeight, setBmrHeight] = useState('');
  const [bmrAge, setBmrAge] = useState('');
  const [bmrGender, setBmrGender] = useState('male');
  const [bmrResult, setBmrResult] = useState(null);
  
  // Body Fat Calculator State
  const [bfWeight, setBfWeight] = useState('');
  const [bfWaist, setBfWaist] = useState('');
  const [bfHip, setBfHip] = useState('');
  const [bfNeck, setBfNeck] = useState('');
  const [bfGender, setBfGender] = useState('male');
  const [bfHeight, setBfHeight] = useState('');
  const [bfResult, setBfResult] = useState(null);
  
  // Lean Body Mass State
  const [lbmWeight, setLbmWeight] = useState('');
  const [lbmBodyFat, setLbmBodyFat] = useState('');
  const [lbmResult, setLbmResult] = useState(null);
  
  // Calorie Needs State
  const [calWeight, setCalWeight] = useState('');
  const [calHeight, setCalHeight] = useState('');
  const [calAge, setCalAge] = useState('');
  const [calGender, setCalGender] = useState('male');
  const [calActivity, setCalActivity] = useState('sedentary');
  const [calResult, setCalResult] = useState(null);
  
  // Ideal Weight State
  const [iwHeight, setIwHeight] = useState('');
  const [iwGender, setIwGender] = useState('male');
  const [iwResult, setIwResult] = useState(null);
  
  // Walking Calories State
  const [wcWeight, setWcWeight] = useState('');
  const [wcDistance, setWcDistance] = useState('');
  const [wcResult, setWcResult] = useState(null);
  
  // Expanded calculator state
  const [expandedCalc, setExpandedCalc] = useState(null);
  const mobileCalcRef = React.useRef(null);

  const handleSelectCalc = (calcId) => {
    const isCollapsing = expandedCalc === calcId;
    setExpandedCalc(isCollapsing ? null : calcId);
    if (!isCollapsing) {
      // Scroll to calculator on mobile after render
      setTimeout(() => {
        mobileCalcRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  // BMR Calculation (Mifflin-St Jeor)
  const calculateBMR = () => {
    const weight = parseFloat(bmrWeight);
    const height = parseFloat(bmrHeight);
    const age = parseFloat(bmrAge);
    
    if (!weight || !height || !age) return;
    
    let bmr;
    if (bmrGender === 'male') {
      bmr = 10 * weight + 6.25 * height - 5 * age + 5;
    } else {
      bmr = 10 * weight + 6.25 * height - 5 * age - 161;
    }
    
    setBmrResult({
      bmr: Math.round(bmr),
      sedentary: Math.round(bmr * 1.2),
      light: Math.round(bmr * 1.375),
      moderate: Math.round(bmr * 1.55),
      active: Math.round(bmr * 1.725),
      veryActive: Math.round(bmr * 1.9)
    });
  };

  // Body Fat Calculation (US Navy Method)
  const calculateBodyFat = () => {
    const waist = parseFloat(bfWaist);
    const neck = parseFloat(bfNeck);
    const height = parseFloat(bfHeight);
    const hip = parseFloat(bfHip);
    const weight = parseFloat(bfWeight);
    
    if (!waist || !neck || !height || !weight) return;
    
    let bodyFat;
    if (bfGender === 'male') {
      bodyFat = 495 / (1.0324 - 0.19077 * Math.log10(waist - neck) + 0.15456 * Math.log10(height)) - 450;
    } else {
      if (!hip) return;
      bodyFat = 495 / (1.29579 - 0.35004 * Math.log10(waist + hip - neck) + 0.22100 * Math.log10(height)) - 450;
    }
    
    const fatMass = (bodyFat / 100) * weight;
    const leanMass = weight - fatMass;
    
    let category = '';
    if (bfGender === 'male') {
      if (bodyFat < 6) category = 'Essential Fat';
      else if (bodyFat < 14) category = 'Athletes';
      else if (bodyFat < 18) category = 'Fitness';
      else if (bodyFat < 25) category = 'Average';
      else category = 'Obese';
    } else {
      if (bodyFat < 14) category = 'Essential Fat';
      else if (bodyFat < 21) category = 'Athletes';
      else if (bodyFat < 25) category = 'Fitness';
      else if (bodyFat < 32) category = 'Average';
      else category = 'Obese';
    }
    
    setBfResult({
      bodyFat: bodyFat.toFixed(1),
      fatMass: fatMass.toFixed(1),
      leanMass: leanMass.toFixed(1),
      category
    });
  };

  // Lean Body Mass Calculation
  const calculateLBM = () => {
    const weight = parseFloat(lbmWeight);
    const bodyFat = parseFloat(lbmBodyFat);
    
    if (!weight || !bodyFat) return;
    
    const fatMass = (bodyFat / 100) * weight;
    const leanMass = weight - fatMass;
    
    setLbmResult({
      leanMass: leanMass.toFixed(1),
      fatMass: fatMass.toFixed(1),
      leanPercent: (100 - bodyFat).toFixed(1)
    });
  };

  // Daily Calorie Needs (TDEE)
  const calculateCalories = () => {
    const weight = parseFloat(calWeight);
    const height = parseFloat(calHeight);
    const age = parseFloat(calAge);
    
    if (!weight || !height || !age) return;
    
    let bmr;
    if (calGender === 'male') {
      bmr = 10 * weight + 6.25 * height - 5 * age + 5;
    } else {
      bmr = 10 * weight + 6.25 * height - 5 * age - 161;
    }
    
    const activityMultipliers = {
      sedentary: 1.2,
      light: 1.375,
      moderate: 1.55,
      active: 1.725,
      veryActive: 1.9
    };
    
    const tdee = bmr * activityMultipliers[calActivity];
    
    setCalResult({
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      weightLoss: Math.round(tdee - 500),
      weightGain: Math.round(tdee + 500)
    });
  };

  // Ideal Body Weight (Devine Formula)
  const calculateIdealWeight = () => {
    const height = parseFloat(iwHeight);
    
    if (!height) return;
    
    const heightInches = height / 2.54;
    const heightOver5ft = heightInches - 60;
    
    let idealKg;
    if (iwGender === 'male') {
      idealKg = 50 + 2.3 * heightOver5ft;
    } else {
      idealKg = 45.5 + 2.3 * heightOver5ft;
    }
    
    // BMI range for healthy weight
    const heightM = height / 100;
    const minWeight = 18.5 * heightM * heightM;
    const maxWeight = 24.9 * heightM * heightM;
    
    setIwResult({
      ideal: idealKg.toFixed(1),
      minHealthy: minWeight.toFixed(1),
      maxHealthy: maxWeight.toFixed(1)
    });
  };

  // Walking Calories Burned
  const calculateWalkingCalories = () => {
    const weight = parseFloat(wcWeight);
    const distance = parseFloat(wcDistance);
    
    if (!weight || !distance) return;
    
    // Average MET for walking is 3.5
    const caloriesPerKm = weight * 0.57;
    const totalCalories = caloriesPerKm * distance;
    
    // Time estimates at different speeds
    const slowTime = distance / 4; // 4 km/h
    const normalTime = distance / 5; // 5 km/h
    const fastTime = distance / 6.5; // 6.5 km/h
    
    setWcResult({
      calories: Math.round(totalCalories),
      slowTime: Math.round(slowTime * 60),
      normalTime: Math.round(normalTime * 60),
      fastTime: Math.round(fastTime * 60)
    });
  };

  const calculators = [
    {
      id: 'bmr',
      title: 'BMR Calculator (Basal Metabolic Rate)',
      description: 'Calculate how many calories your body burns at rest. Uses Mifflin-St Jeor formula.',
      icon: Flame,
      gradient: 'from-orange-500 to-red-500',
      seoKeywords: 'BMR calculator, basal metabolic rate calculator, resting metabolic rate, calorie burn at rest'
    },
    {
      id: 'bodyfat',
      title: 'Body Fat Percentage Calculator',
      description: 'Estimate body fat % using US Navy method. More accurate than BMI.',
      icon: Scale,
      gradient: 'from-blue-500 to-cyan-500',
      seoKeywords: 'body fat calculator, body fat percentage calculator, navy body fat calculator, body composition'
    },
    {
      id: 'lbm',
      title: 'Lean Body Mass Calculator (LBM)',
      description: 'Calculate your muscle mass and fat-free weight for better fitness tracking.',
      icon: Activity,
      gradient: 'from-green-500 to-emerald-500',
      seoKeywords: 'lean body mass calculator, LBM calculator, muscle mass calculator, fat free mass'
    },
    {
      id: 'calories',
      title: 'Daily Calorie Calculator (TDEE)',
      description: 'Find how many calories you need daily based on activity level for weight loss or gain.',
      icon: Target,
      gradient: 'from-violet-500 to-purple-500',
      seoKeywords: 'TDEE calculator, calorie calculator, daily calorie needs, maintenance calories, calorie deficit'
    },
    {
      id: 'idealweight',
      title: 'Ideal Body Weight Calculator',
      description: 'Calculate your ideal weight for your height using the Devine formula.',
      icon: Ruler,
      gradient: 'from-pink-500 to-rose-500',
      seoKeywords: 'ideal body weight calculator, healthy weight calculator, ideal weight for height, what should I weigh'
    },
    {
      id: 'walking',
      title: 'Walking Calories Burned Calculator',
      description: 'Calculate how many calories walking burns based on distance and your weight.',
      icon: Footprints,
      gradient: 'from-amber-500 to-orange-500',
      seoKeywords: 'walking calories calculator, calories burned walking, steps calories, walking for weight loss'
    }
  ];

  const renderCalculatorContent = (calcId) => {
    switch (calcId) {
      case 'bmr':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400 block mb-1">Weight (kg)</label>
                <input
                  type="number"
                  value={bmrWeight}
                  onChange={(e) => setBmrWeight(e.target.value)}
                  className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  placeholder="70"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Height (cm)</label>
                <input
                  type="number"
                  value={bmrHeight}
                  onChange={(e) => setBmrHeight(e.target.value)}
                  className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  placeholder="175"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Age</label>
                <input
                  type="number"
                  value={bmrAge}
                  onChange={(e) => setBmrAge(e.target.value)}
                  className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  placeholder="30"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Gender</label>
                <select
                  value={bmrGender}
                  onChange={(e) => setBmrGender(e.target.value)}
                  className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
            </div>
            <Button onClick={calculateBMR} className="w-full bg-gradient-to-r from-orange-500 to-red-500">
              Calculate BMR
            </Button>
            {bmrResult && (
              <div className="p-4 bg-slate-800/50 rounded-xl space-y-3">
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-400">{bmrResult.bmr} kcal</div>
                  <div className="text-slate-400">Basal Metabolic Rate</div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 bg-slate-800 rounded">
                    <div className="text-slate-400">Sedentary</div>
                    <div className="font-bold">{bmrResult.sedentary} kcal</div>
                  </div>
                  <div className="p-2 bg-slate-800 rounded">
                    <div className="text-slate-400">Light Activity</div>
                    <div className="font-bold">{bmrResult.light} kcal</div>
                  </div>
                  <div className="p-2 bg-slate-800 rounded">
                    <div className="text-slate-400">Moderate</div>
                    <div className="font-bold">{bmrResult.moderate} kcal</div>
                  </div>
                  <div className="p-2 bg-slate-800 rounded">
                    <div className="text-slate-400">Very Active</div>
                    <div className="font-bold">{bmrResult.veryActive} kcal</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      
      case 'bodyfat':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400 block mb-1">Weight (kg)</label>
                <input
                  type="number"
                  value={bfWeight}
                  onChange={(e) => setBfWeight(e.target.value)}
                  className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  placeholder="70"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Height (cm)</label>
                <input
                  type="number"
                  value={bfHeight}
                  onChange={(e) => setBfHeight(e.target.value)}
                  className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  placeholder="175"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Waist (cm)</label>
                <input
                  type="number"
                  value={bfWaist}
                  onChange={(e) => setBfWaist(e.target.value)}
                  className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  placeholder="85"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Neck (cm)</label>
                <input
                  type="number"
                  value={bfNeck}
                  onChange={(e) => setBfNeck(e.target.value)}
                  className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  placeholder="38"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Gender</label>
                <select
                  value={bfGender}
                  onChange={(e) => setBfGender(e.target.value)}
                  className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              {bfGender === 'female' && (
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Hip (cm)</label>
                  <input
                    type="number"
                    value={bfHip}
                    onChange={(e) => setBfHip(e.target.value)}
                    className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
                    placeholder="95"
                  />
                </div>
              )}
            </div>
            <Button onClick={calculateBodyFat} className="w-full bg-gradient-to-r from-blue-500 to-cyan-500">
              Calculate Body Fat
            </Button>
            {bfResult && (
              <div className="p-4 bg-slate-800/50 rounded-xl space-y-3">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-400">{bfResult.bodyFat}%</div>
                  <div className="text-slate-400">Body Fat Percentage</div>
                  <div className={`text-sm mt-1 ${
                    bfResult.category === 'Fitness' || bfResult.category === 'Athletes' 
                      ? 'text-green-400' 
                      : bfResult.category === 'Average' 
                        ? 'text-yellow-400' 
                        : 'text-red-400'
                  }`}>
                    Category: {bfResult.category}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 bg-slate-800 rounded">
                    <div className="text-slate-400">Fat Mass</div>
                    <div className="font-bold">{bfResult.fatMass} kg</div>
                  </div>
                  <div className="p-2 bg-slate-800 rounded">
                    <div className="text-slate-400">Lean Mass</div>
                    <div className="font-bold">{bfResult.leanMass} kg</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      
      case 'lbm':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400 block mb-1">Weight (kg)</label>
                <input
                  type="number"
                  value={lbmWeight}
                  onChange={(e) => setLbmWeight(e.target.value)}
                  className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  placeholder="70"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Body Fat (%)</label>
                <input
                  type="number"
                  value={lbmBodyFat}
                  onChange={(e) => setLbmBodyFat(e.target.value)}
                  className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  placeholder="20"
                />
              </div>
            </div>
            <Button onClick={calculateLBM} className="w-full bg-gradient-to-r from-green-500 to-emerald-500">
              Calculate Lean Body Mass
            </Button>
            {lbmResult && (
              <div className="p-4 bg-slate-800/50 rounded-xl space-y-3">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-400">{lbmResult.leanMass} kg</div>
                  <div className="text-slate-400">Lean Body Mass</div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 bg-slate-800 rounded">
                    <div className="text-slate-400">Fat Mass</div>
                    <div className="font-bold">{lbmResult.fatMass} kg</div>
                  </div>
                  <div className="p-2 bg-slate-800 rounded">
                    <div className="text-slate-400">Lean %</div>
                    <div className="font-bold">{lbmResult.leanPercent}%</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      
      case 'calories':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400 block mb-1">Weight (kg)</label>
                <input
                  type="number"
                  value={calWeight}
                  onChange={(e) => setCalWeight(e.target.value)}
                  className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  placeholder="70"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Height (cm)</label>
                <input
                  type="number"
                  value={calHeight}
                  onChange={(e) => setCalHeight(e.target.value)}
                  className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  placeholder="175"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Age</label>
                <input
                  type="number"
                  value={calAge}
                  onChange={(e) => setCalAge(e.target.value)}
                  className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  placeholder="30"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Gender</label>
                <select
                  value={calGender}
                  onChange={(e) => setCalGender(e.target.value)}
                  className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Activity Level</label>
              <select
                value={calActivity}
                onChange={(e) => setCalActivity(e.target.value)}
                className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
              >
                <option value="sedentary">Sedentary (little or no exercise)</option>
                <option value="light">Light (exercise 1-3 days/week)</option>
                <option value="moderate">Moderate (exercise 3-5 days/week)</option>
                <option value="active">Active (exercise 6-7 days/week)</option>
                <option value="veryActive">Very Active (hard exercise daily)</option>
              </select>
            </div>
            <Button onClick={calculateCalories} className="w-full bg-gradient-to-r from-violet-500 to-purple-500">
              Calculate Calories
            </Button>
            {calResult && (
              <div className="p-4 bg-slate-800/50 rounded-xl space-y-3">
                <div className="text-center">
                  <div className="text-3xl font-bold text-violet-400">{calResult.tdee} kcal</div>
                  <div className="text-slate-400">Daily Calorie Needs (TDEE)</div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="p-2 bg-slate-800 rounded text-center">
                    <div className="text-red-400 font-bold">{calResult.weightLoss}</div>
                    <div className="text-slate-400 text-xs">Weight Loss</div>
                  </div>
                  <div className="p-2 bg-slate-800 rounded text-center">
                    <div className="text-violet-400 font-bold">{calResult.tdee}</div>
                    <div className="text-slate-400 text-xs">Maintain</div>
                  </div>
                  <div className="p-2 bg-slate-800 rounded text-center">
                    <div className="text-green-400 font-bold">{calResult.weightGain}</div>
                    <div className="text-slate-400 text-xs">Weight Gain</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      
      case 'idealweight':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400 block mb-1">Height (cm)</label>
                <input
                  type="number"
                  value={iwHeight}
                  onChange={(e) => setIwHeight(e.target.value)}
                  className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  placeholder="175"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Gender</label>
                <select
                  value={iwGender}
                  onChange={(e) => setIwGender(e.target.value)}
                  className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
            </div>
            <Button onClick={calculateIdealWeight} className="w-full bg-gradient-to-r from-pink-500 to-rose-500">
              Calculate Ideal Weight
            </Button>
            {iwResult && (
              <div className="p-4 bg-slate-800/50 rounded-xl space-y-3">
                <div className="text-center">
                  <div className="text-3xl font-bold text-pink-400">{iwResult.ideal} kg</div>
                  <div className="text-slate-400">Ideal Body Weight</div>
                </div>
                <div className="text-center text-sm">
                  <div className="text-slate-400">Healthy BMI Range</div>
                  <div className="font-bold">{iwResult.minHealthy} - {iwResult.maxHealthy} kg</div>
                </div>
              </div>
            )}
          </div>
        );
      
      case 'walking':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400 block mb-1">Weight (kg)</label>
                <input
                  type="number"
                  value={wcWeight}
                  onChange={(e) => setWcWeight(e.target.value)}
                  className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  placeholder="70"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Distance (km)</label>
                <input
                  type="number"
                  value={wcDistance}
                  onChange={(e) => setWcDistance(e.target.value)}
                  className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  placeholder="5"
                />
              </div>
            </div>
            <Button onClick={calculateWalkingCalories} className="w-full bg-gradient-to-r from-amber-500 to-orange-500">
              Calculate Calories
            </Button>
            {wcResult && (
              <div className="p-4 bg-slate-800/50 rounded-xl space-y-3">
                <div className="text-center">
                  <div className="text-3xl font-bold text-amber-400">{wcResult.calories} kcal</div>
                  <div className="text-slate-400">Calories Burned</div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="p-2 bg-slate-800 rounded text-center">
                    <div className="font-bold">{wcResult.slowTime} min</div>
                    <div className="text-slate-400 text-xs">Slow (4 km/h)</div>
                  </div>
                  <div className="p-2 bg-slate-800 rounded text-center">
                    <div className="font-bold">{wcResult.normalTime} min</div>
                    <div className="text-slate-400 text-xs">Normal (5 km/h)</div>
                  </div>
                  <div className="p-2 bg-slate-800 rounded text-center">
                    <div className="font-bold">{wcResult.fastTime} min</div>
                    <div className="text-slate-400 text-xs">Fast (6.5 km/h)</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-950/80 backdrop-blur-lg border-none ios-header" style={{ border: 'none' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
            <Calculator className="h-4 w-4 text-white" />
          </div>
          <span className="font-bold">Fitness Calculators</span>
        </div>
      </header>

      {/* Hero Section with SEO content — hidden on mobile when calculator is expanded */}
      <section className={`py-12 px-4 ${expandedCalc ? 'hidden sm:block' : ''}`}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 mb-4">
            <Calculator className="h-4 w-4 text-blue-400" />
            <span className="text-sm text-blue-400">100% Free Online Calculators</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">
            Free Fitness & Health Calculators
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto mb-4">
            Calculate your BMR, body fat percentage, lean body mass, daily calorie needs, 
            ideal weight, and walking calories burned. All calculators are <strong className="text-white">free to use</strong> with no signup required.
          </p>
          <p className="text-slate-500 text-sm max-w-xl mx-auto">
            Accurate results using scientifically-proven formulas: Mifflin-St Jeor equation, 
            US Navy body fat method, and Devine ideal weight formula.
          </p>
        </div>
      </section>

      {/* Calculator Cards Grid */}
      <section className="pb-8 px-4">
        {/* Mobile: When a calculator is expanded, show only that calculator */}
        {expandedCalc && (
          <div className="sm:hidden max-w-5xl mx-auto" ref={mobileCalcRef} style={{ scrollMarginTop: '60px' }}>
            <button 
              onClick={() => setExpandedCalc(null)}
              className="flex items-center gap-1 text-sm text-slate-400 hover:text-white mb-4 transition-colors"
              data-testid="calc-back-btn"
            >
              <ChevronLeft className="h-4 w-4" />
              All Calculators
            </button>
            {(() => {
              const calc = calculators.find(c => c.id === expandedCalc);
              if (!calc) return null;
              return (
                <div className="rounded-2xl border border-blue-500/50 bg-slate-900 ring-1 ring-blue-500/30 p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${calc.gradient} flex items-center justify-center flex-shrink-0`}>
                      <calc.icon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-base">{calc.title}</h2>
                      <p className="text-xs text-slate-400">{calc.description}</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-800">
                    {renderCalculatorContent(expandedCalc)}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Mobile: Card grid (hidden when a calculator is expanded) */}
        <div className={`sm:hidden max-w-5xl mx-auto grid grid-cols-1 gap-4 ${expandedCalc ? 'hidden' : ''}`}>
          {calculators.map((calc) => (
            <button
              key={calc.id}
              onClick={() => handleSelectCalc(calc.id)}
              className="text-left p-5 rounded-2xl border border-slate-800 bg-slate-900/50 hover:bg-slate-900 hover:border-slate-700 transition-all duration-200"
              data-testid={`calc-card-${calc.id}`}
            >
              <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${calc.gradient} flex items-center justify-center mb-3`}>
                <calc.icon className="h-5 w-5 text-white" />
              </div>
              <h2 className="font-semibold text-sm leading-tight mb-1">{calc.title}</h2>
              <p className="text-xs text-slate-400 leading-relaxed">{calc.description}</p>
            </button>
          ))}
        </div>

        {/* Desktop/Tablet: Always show the 3-column grid */}
        <div className="hidden sm:grid max-w-5xl mx-auto grid-cols-2 lg:grid-cols-3 gap-4">
          {calculators.map((calc) => (
            <button
              key={calc.id}
              onClick={() => handleSelectCalc(calc.id)}
              className={`text-left p-5 rounded-2xl border transition-all duration-200 ${
                expandedCalc === calc.id 
                  ? 'border-blue-500/50 bg-slate-900 ring-1 ring-blue-500/30' 
                  : 'border-slate-800 bg-slate-900/50 hover:bg-slate-900 hover:border-slate-700'
              }`}
              data-testid={`calc-card-desktop-${calc.id}`}
            >
              <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${calc.gradient} flex items-center justify-center mb-3`}>
                <calc.icon className="h-5 w-5 text-white" />
              </div>
              <h2 className="font-semibold text-sm leading-tight mb-1">{calc.title}</h2>
              <p className="text-xs text-slate-400 leading-relaxed">{calc.description}</p>
            </button>
          ))}
        </div>
      </section>

      {/* Desktop/Tablet: Expanded Calculator Content (below the grid) */}
      {expandedCalc && (
        <section className="hidden sm:block pb-12 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
              <div className="flex items-center gap-3 mb-5">
                {(() => {
                  const calc = calculators.find(c => c.id === expandedCalc);
                  if (!calc) return null;
                  return (
                    <>
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${calc.gradient} flex items-center justify-center flex-shrink-0`}>
                        <calc.icon className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="font-semibold text-lg">{calc.title}</h3>
                    </>
                  );
                })()}
              </div>
              {renderCalculatorContent(expandedCalc)}
            </div>
          </div>
        </section>
      )}

      {/* SEO Content Section */}
      <section className="py-12 px-4 bg-slate-900/30 border-t border-slate-800">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-center">
            About Our Free Health & Fitness Calculators
          </h2>
          <div className="grid md:grid-cols-2 gap-6 text-sm text-slate-400">
            <div>
              <h3 className="text-white font-semibold mb-2">BMR Calculator & TDEE Calculator</h3>
              <p>
                Calculate your <strong>Basal Metabolic Rate (BMR)</strong> - how many calories your body burns at rest. 
                Our <strong>TDEE calculator</strong> shows Total Daily Energy Expenditure based on activity level. 
                Perfect for <strong>calorie deficit calculator</strong> planning and <strong>weight loss calorie calculator</strong> goals.
                Uses the accurate Mifflin-St Jeor equation.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-2">Body Fat Percentage Calculator</h3>
              <p>
                Free <strong>body fat calculator</strong> using the US Navy method. This <strong>body composition calculator</strong> estimates 
                your fat percentage from waist, neck, and height measurements. Better than BMI for tracking 
                <strong> fat loss progress</strong>. Works as a <strong>navy body fat calculator</strong> with proven accuracy.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-2">Lean Body Mass Calculator</h3>
              <p>
                Calculate your <strong>lean body mass (LBM)</strong> - total weight minus fat. Essential for 
                <strong> muscle mass calculator</strong> tracking and <strong>body recomposition</strong>. Know your <strong>fat free mass</strong> 
                to optimize protein intake and track <strong>muscle gain</strong> progress accurately.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-2">Calorie Intake Calculator</h3>
              <p>
                <strong>How many calories should I eat?</strong> Our <strong>daily calorie calculator</strong> answers this based on 
                your activity level. Shows <strong>maintenance calories</strong>, <strong>calories to lose weight</strong>, and 
                <strong> calories to gain muscle</strong>. Works as a <strong>macro calculator</strong> starting point.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-2">Ideal Body Weight Calculator</h3>
              <p>
                Find your <strong>ideal weight for height</strong> using the Devine formula. This <strong>healthy weight calculator</strong> 
                shows both ideal weight and BMI-based healthy range. Answer "<strong>what should I weigh</strong>" with science-backed formulas.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-2">Walking Calories Burned Calculator</h3>
              <p>
                <strong>How many calories does walking burn?</strong> Our <strong>walking calorie calculator</strong> estimates 
                <strong> calories burned walking</strong> by distance and weight. Perfect for <strong>10000 steps calories</strong> tracking 
                and <strong>walking for weight loss</strong> planning. Includes time estimates at different speeds.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Additional SEO Keywords Section */}
      <section className="py-10 px-4 border-t border-slate-800">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl font-bold mb-4 text-center">
            Why Use Our Free Fitness Calculators?
          </h2>
          <div className="text-sm text-slate-400 space-y-4">
            <p>
              Our <strong>free online fitness calculators</strong> help you plan your health journey with precision. Whether you're 
              asking "<strong>how many calories do I need to lose weight</strong>" or wondering about your <strong>body fat percentage</strong>, 
              we provide instant, accurate results using proven scientific formulas.
            </p>
            <p>
              Unlike basic <strong>BMI calculators</strong>, our tools give you actionable insights. The <strong>TDEE calculator</strong> accounts 
              for your activity level, while our <strong>body fat calculator</strong> uses the military-standard Navy method. 
              Calculate your <strong>calorie deficit</strong> for weight loss or <strong>calorie surplus</strong> for muscle building.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 text-xs text-slate-500">
              <div className="text-center p-2 bg-slate-800/30 rounded">BMR Calculator Free</div>
              <div className="text-center p-2 bg-slate-800/30 rounded">TDEE Calculator</div>
              <div className="text-center p-2 bg-slate-800/30 rounded">Body Fat % Calculator</div>
              <div className="text-center p-2 bg-slate-800/30 rounded">Calorie Deficit Calculator</div>
              <div className="text-center p-2 bg-slate-800/30 rounded">Ideal Weight Calculator</div>
              <div className="text-center p-2 bg-slate-800/30 rounded">Walking Calories Burned</div>
              <div className="text-center p-2 bg-slate-800/30 rounded">Lean Body Mass Calculator</div>
              <div className="text-center p-2 bg-slate-800/30 rounded">Macro Calculator</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-4">
            Want to Track Your Calories & Fitness Progress?
          </h2>
          <p className="text-slate-400 mb-6">
            Use NutriSteps - the <strong className="text-white">all in one fitness app</strong> with <strong className="text-white">AI calorie counter</strong>. 
            Snap photos of meals, scan restaurant menus, get AI workout plans. Your complete nutrition tracker.
          </p>
          <Button 
            onClick={onBack}
            className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
            data-testid="try-nutristeps-btn"
          >
            Try NutriSteps Free - 14 Day Trial
          </Button>
          <p className="text-slate-500 text-xs mt-3">No credit card required • Cancel anytime</p>
        </div>
      </section>

      {/* FAQ Section for SEO */}
      <section className="py-12 px-4 bg-slate-900/30 border-t border-slate-800">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl font-bold mb-6 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4 text-sm">
            <div className="p-4 bg-slate-800/30 rounded-xl">
              <h3 className="text-white font-semibold mb-2">How accurate is the BMR calculator?</h3>
              <p className="text-slate-400">Our BMR calculator uses the Mifflin-St Jeor equation, considered the most accurate formula for estimating basal metabolic rate. It's typically within 10% of actual BMR for most people.</p>
            </div>
            <div className="p-4 bg-slate-800/30 rounded-xl">
              <h3 className="text-white font-semibold mb-2">What's the difference between BMR and TDEE?</h3>
              <p className="text-slate-400">BMR (Basal Metabolic Rate) is calories burned at complete rest. TDEE (Total Daily Energy Expenditure) includes your activity level - it's your actual daily calorie burn and what you should base your diet on.</p>
            </div>
            <div className="p-4 bg-slate-800/30 rounded-xl">
              <h3 className="text-white font-semibold mb-2">How many calories should I eat to lose weight?</h3>
              <p className="text-slate-400">For safe weight loss, eat 500 calories below your TDEE. This creates a calorie deficit of 3,500 calories per week, resulting in approximately 0.5kg (1 lb) of fat loss weekly.</p>
            </div>
            <div className="p-4 bg-slate-800/30 rounded-xl">
              <h3 className="text-white font-semibold mb-2">Is body fat percentage better than BMI?</h3>
              <p className="text-slate-400">Yes, body fat percentage is more accurate than BMI for assessing health. BMI doesn't distinguish between muscle and fat, so muscular people may have "overweight" BMI despite being healthy.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CalculatorsPage;
